import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot", { email });
      setSent(true);
      // Dev convenience: backend returns token until Resend is wired
      if (data.reset_token) setToken(data.reset_token);
      toast.success("If this email exists, a reset link has been sent");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-8 shadow-xl shadow-slate-900/5" data-testid="forgot-form">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center"><HardHat className="w-5 h-5 text-white" /></div>
          <div className="font-display font-extrabold uppercase tracking-widest">Quick Quotes</div>
        </Link>
        <h1 className="font-display text-2xl font-extrabold">Forgot password</h1>
        <p className="text-sm text-slate-500 mt-1">Enter your email and we'll send a reset link.</p>

        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white" data-testid="forgot-submit">
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </div>

        {sent && token && (
          <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="reset-link-dev">
            <div className="font-bold uppercase tracking-wider">Dev mode</div>
            Email delivery isn't wired yet. Use this one-time reset link:
            <button onClick={() => nav(`/reset-password?token=${token}`)} className="mt-2 w-full text-left bg-white border border-amber-300 rounded px-2 py-1 font-mono text-[10px] break-all hover:bg-amber-100">
              {`${window.location.origin}/reset-password?token=${token}`}
            </button>
          </div>
        )}

        <p className="mt-6 text-sm text-slate-500 text-center">
          Remembered it? <Link to="/login" className="text-blue-600 font-medium hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
